import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  ClipboardList,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
  ShieldAlert,
} from 'lucide-react';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'safeguarding', label: 'Safeguarding' },
  { value: 'missing', label: 'Missing' },
  { value: 'warning', label: 'Warning' },
];

function formatDate(value) {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function labelize(value) {
  return String(value || 'general')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function severityMeta(severity) {
  const normalized = String(severity || '').toLowerCase();
  if (normalized === 'urgent') {
    return {
      label: 'Urgent',
      chip: 'bg-red-50 text-red-700 border-red-200',
      panel: 'border-red-200 bg-red-50',
      icon: AlertTriangle,
      iconClass: 'text-red-600',
    };
  }
  if (normalized === 'safeguarding') {
    return {
      label: 'Safeguarding',
      chip: 'bg-rose-50 text-rose-700 border-rose-200',
      panel: 'border-rose-200 bg-rose-50',
      icon: ShieldAlert,
      iconClass: 'text-rose-600',
    };
  }
  if (normalized === 'warning') {
    return {
      label: 'Warning',
      chip: 'bg-amber-50 text-amber-700 border-amber-200',
      panel: 'border-amber-200 bg-amber-50',
      icon: CalendarClock,
      iconClass: 'text-amber-600',
    };
  }
  if (normalized === 'missing') {
    return {
      label: 'Missing',
      chip: 'bg-slate-100 text-slate-700 border-slate-200',
      panel: 'border-slate-200 bg-slate-50',
      icon: ClipboardList,
      iconClass: 'text-slate-600',
    };
  }
  return {
    label: 'Notice',
    chip: 'bg-gray-100 text-gray-700 border-gray-200',
    panel: 'border-gray-200 bg-gray-50',
    icon: Bell,
    iconClass: 'text-gray-600',
  };
}

export default function ComplianceAlertsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ urgent: 0, safeguarding: 0, missing: 0, warning: 0 });
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [asOf, setAsOf] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [hasSuccessfulLoad, setHasSuccessfulLoad] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/compliance/alerts-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 500 },
      });
      const payload = response.data;
      // A malformed/partial response is not proof that the workspace has zero alerts.
      const completeCounts = ['urgent', 'safeguarding', 'missing', 'warning'].every(
        (category) => Number.isInteger(payload?.counts?.[category]) && payload.counts[category] >= 0,
      );
      // The API always returns all four severities and its as-of timestamp.
      // Missing counts/time cannot be interpreted as a verified zero.
      if (!payload || !Array.isArray(payload.alerts) || !completeCounts ||
          !payload.as_of || Number.isNaN(Date.parse(payload.as_of)) || payload.error) {
        throw new Error('Incomplete compliance alerts response');
      }
      setAlerts(payload.alerts);
      setCounts(payload.counts);
      setAsOf(payload.as_of || null);
      setHasSuccessfulLoad(true);
      setLoadError(false);
    } catch (error) {
      console.error('Failed to load compliance alerts:', error);
      // Preserve the last successful snapshot as *stale*, never invent an all-clear.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const filteredAlerts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return alerts.filter((row) => {
      const severityMatches = severityFilter === 'all' || String(row.severity || '').toLowerCase() === severityFilter;
      if (!severityMatches) return false;
      if (!query) return true;
      const text = [
        row.title,
        row.category,
        row.entity_name,
        row.entity_id,
        row.source,
      ].filter(Boolean).join(' ').toLowerCase();
      return text.includes(query);
    });
  }, [alerts, searchTerm, severityFilter]);

  const priorityAlerts = useMemo(() => (
    alerts
      .filter((row) => ['urgent', 'safeguarding'].includes(String(row.severity || '').toLowerCase()))
      .slice(0, 5)
  ), [alerts]);

  const countTiles = [
    { key: 'urgent', label: 'Urgent', helper: 'Expired or overdue now' },
    { key: 'safeguarding', label: 'Safeguarding', helper: 'Open concerns and follow-up' },
    { key: 'missing', label: 'Missing', helper: 'Evidence or checks absent' },
    { key: 'warning', label: 'Warning', helper: 'Expiring soon' },
  ];
  const totalActive = Object.values(counts || {}).reduce((sum, value) => sum + Number(value || 0), 0);

  return (
    <div className="space-y-5" data-testid="compliance-command-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
            <Bell className="h-3.5 w-3.5" />
            Live compliance command
          </div>
          <h1 className="mt-3 text-2xl font-semibold text-text-primary">Compliance Command</h1>
          <p className="mt-1 max-w-2xl text-sm text-text-muted">
            One queue for expiring evidence, missing checks, safeguarding follow-up and ongoing staff compliance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {asOf && <span className="text-xs text-text-muted">{loadError ? 'Last successful update' : 'Updated'} {formatDate(asOf)}</span>}
          <Button variant="outline" className="rounded-xl" onClick={fetchAlerts} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </div>

      {loadError && (
        <div role="alert" data-testid="compliance-alerts-unavailable" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">Compliance checks unavailable</p>
              <p className="mt-1 text-sm">
                {hasSuccessfulLoad
                  ? 'Showing the last successful snapshot. It may be out of date; do not treat it as approval or an all-clear.'
                  : 'No reliable compliance results could be loaded. Do not treat this as an all-clear or approve work from this page.'}
              </p>
              <Button type="button" variant="outline" className="mt-3 min-h-11 rounded-xl" onClick={fetchAlerts} disabled={loading}>
                {loading ? 'Retrying…' : 'Retry checks'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {countTiles.map((tile) => {
          const meta = severityMeta(tile.key);
          const Icon = meta.icon;
          return (
            <button
              key={tile.key}
              type="button"
              onClick={() => setSeverityFilter(tile.key)}
              aria-pressed={severityFilter === tile.key}
              className={`rounded-xl border p-4 text-left transition hover:shadow-sm ${severityFilter === tile.key ? meta.panel : 'border-[#E4E8EB] bg-white'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-text-muted">{tile.label}</div>
                  <div className="mt-2 text-3xl font-semibold text-text-primary">{hasSuccessfulLoad ? (counts[tile.key] || 0) : '—'}</div>
                </div>
                <Icon className={`h-5 w-5 ${meta.iconClass}`} />
              </div>
              <p className="mt-2 text-xs text-text-muted">{tile.helper}</p>
            </button>
          );
        })}
      </div>

      {totalActive === 0 && !loading && !loadError && hasSuccessfulLoad ? (
        <div role="status" className="rounded-xl border border-slate-200 bg-slate-50 p-5">
          <div className="flex items-start gap-3">
            <ClipboardList className="mt-0.5 h-5 w-5 text-slate-600" aria-hidden="true" />
            <div>
              <h2 className="font-medium text-slate-900">No alerts returned in this summary</h2>
              <p className="mt-1 text-sm text-slate-700">
                This summary is limited and is not a compliance clearance. Review individual staff and service-user records before making work-readiness or safeguarding decisions.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {priorityAlerts.length > 0 && (
        <Card className="border-[#E4E8EB] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Priority Queue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {priorityAlerts.map((row, idx) => {
              const meta = severityMeta(row.severity);
              const Icon = meta.icon;
              return (
                <button
                  key={`${row.source || 'src'}-${row.entity_id || idx}-priority`}
                  type="button"
                  onClick={() => navigate(row.link_target || '/portal/compliance-centre')}
                  className={`flex w-full flex-col gap-3 rounded-xl border p-4 text-left transition hover:shadow-sm sm:flex-row sm:items-center sm:justify-between ${meta.panel}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className={`mt-0.5 h-5 w-5 ${meta.iconClass}`} />
                    <div>
                      <p className="font-medium text-text-primary">{row.title || 'Compliance alert'}</p>
                      <p className="mt-1 text-sm text-text-muted">
                        {row.entity_name || row.entity_id || '-'} - {labelize(row.category)}
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                    Open <ExternalLink className="h-4 w-4" />
                  </span>
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E4E8EB] shadow-sm">
        <CardHeader className="gap-3 pb-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="text-base font-medium">All Alerts</CardTitle>
            <p className="mt-1 text-xs text-text-muted">
              {filteredAlerts.length} item{filteredAlerts.length === 1 ? '' : 's'} in view
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="h-10 w-full rounded-xl border border-[#D7DEE3] bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 sm:w-64"
                placeholder="Search person, source, category"
              />
            </div>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-full rounded-xl sm:w-48">
                <SelectValue placeholder="Filter severity" />
              </SelectTrigger>
              <SelectContent>
                {FILTER_OPTIONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="h-40 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="px-6 py-8 text-sm text-text-muted">{loadError ? 'Compliance results are unavailable. Retry checks.' : 'No alerts match this view.'}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E4E8EB] bg-[#F8FAFA]">
                    <th className="px-4 py-3 text-left font-medium text-text-muted">Alert</th>
                    <th className="px-4 py-3 text-left font-medium text-text-muted whitespace-nowrap">Severity</th>
                    <th className="px-4 py-3 text-left font-medium text-text-muted whitespace-nowrap">Category</th>
                    <th className="px-4 py-3 text-left font-medium text-text-muted">Person / Record</th>
                    <th className="px-4 py-3 text-left font-medium text-text-muted whitespace-nowrap">Due / Expiry</th>
                    <th className="px-4 py-3 text-left font-medium text-text-muted whitespace-nowrap">Source</th>
                    <th className="px-4 py-3 text-right font-medium text-text-muted whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlerts.map((row, idx) => {
                    const dateValue = row.due_date || row.expiry_date;
                    const meta = severityMeta(row.severity);
                    return (
                      <tr key={`${row.source || 'src'}-${row.entity_id || idx}-${idx}`} className="border-b border-[#E4E8EB] hover:bg-[#F8FAFA] transition-colors">
                        <td className="px-4 py-3 text-text-primary">{row.title || 'Alert'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${meta.chip}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-text-muted whitespace-nowrap">{labelize(row.category)}</td>
                        <td className="px-4 py-3 text-text-muted">
                          <div className="font-medium text-text-primary">{row.entity_name || row.entity_id || '-'}</div>
                          <div className="text-xs">{row.entity_type || 'entity'} - {row.entity_id || '-'}</div>
                        </td>
                        <td className="px-4 py-3 text-text-muted whitespace-nowrap">{formatDate(dateValue)}</td>
                        <td className="px-4 py-3 text-text-muted whitespace-nowrap">{labelize(row.source)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <Button
                            variant="outline"
                            className="rounded-xl"
                            onClick={() => navigate(row.link_target || '/portal/compliance-centre')}
                          >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Open
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
